import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { Tick02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { PromoSize, PromoContentComponentProps } from "../types";
import { cx } from "shared/utils";
import { PROMO_FEATURES } from "../constants";
import { Card } from "shared/components/Card";
import { useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { markPromoAsSeen, trackPromoRUM } from "../utils";

type PromoSizeProps = Record<
  PromoSize,
  {
    modalContainerClassNames: string;
    imgClassNames: string;
    titleClassNames: string;
    subTitleClassNames: string;
    textClassNames: string;
    contentContainerClassNames: string;
    titleSpacingClassNames: string;
    buttonSize: ButtonSize;
    closeButtonClassNames?: string;
    actionButtonClassNames?: string;
  }
>;

const PROMO_SIZE_PROPS: PromoSizeProps = {
  [PromoSize.XS]: {
    modalContainerClassNames: "max-w-[292px]",
    imgClassNames: "max-h-[168px] rounded-lg p-4 pb-0",
    titleClassNames: "text-sm",
    subTitleClassNames: "text-xs font-regular text-content-300",
    textClassNames: "text-xs font-regular text-content-300",
    contentContainerClassNames: "h-[calc(100%-136px)] p-4 pt-0 mt-[10px] gap-4",
    titleSpacingClassNames: "space-y-1",
    buttonSize: ButtonSize.XS
  },
  [PromoSize.SM]: {
    modalContainerClassNames: "max-w-[400px]",
    imgClassNames: "max-h-[240px]",
    titleClassNames: "text-xl",
    subTitleClassNames: "text-sm font-regular text-content-500",
    textClassNames: "text-sm font-regular text-content-500",
    contentContainerClassNames: "h-[calc(100%-240px)] p-6 gap-6",
    titleSpacingClassNames: "space-y-2",
    buttonSize: ButtonSize.BASE,
    closeButtonClassNames: "!w-[125px]",
    actionButtonClassNames: "w-[121px]"
  },
  [PromoSize.BASE]: {
    modalContainerClassNames: "max-w-[640px]",
    imgClassNames: "max-h-[320px]",
    titleClassNames: "text-xl",
    subTitleClassNames: "text-sm font-regular text-content-500",
    textClassNames: "text-sm font-regular text-content-500",
    contentContainerClassNames: "h-[calc(100%-320px)] p-6 gap-6",
    titleSpacingClassNames: "space-y-2",
    buttonSize: ButtonSize.BASE,
    closeButtonClassNames: "!w-[125px]",
    actionButtonClassNames: "w-[131px]"
  }
};

export const PromoContent = ({
  featureName,
  promoType,
  size,
  onClose,
  onActionButtonClick,
  contentOverrides
}: PromoContentComponentProps) => {
  const featureContent = PROMO_FEATURES[featureName][size];
  const merged = contentOverrides
    ? { ...featureContent, ...contentOverrides }
    : featureContent;
  const {
    imageSrc,
    icons,
    title,
    subtitle,
    description,
    bulletPoints,
    bulletIcon,
    onCloseClassNames,
    closeButtonTitle,
    actionButtonTitle,
    actionButtonLink,
    actionButtonRoute,
    actionButtonClassNames: contentActionButtonClassNames
  } = merged;
  const {
    modalContainerClassNames,
    imgClassNames,
    titleClassNames,
    subTitleClassNames,
    textClassNames,
    contentContainerClassNames,
    titleSpacingClassNames,
    buttonSize,
    closeButtonClassNames,
    actionButtonClassNames
  } = PROMO_SIZE_PROPS[size];
  const navigate = useNavigate();

  useLayoutEffect(() => {
    trackPromoRUM(featureName, promoType, "show");
  }, [promoType, featureName]);

  return (
    <Card className={cx("w-full h-auto border-none", modalContainerClassNames)}>
      <Show if={imageSrc}>
        <img
          src={imageSrc}
          alt="attachment"
          className={cx("w-full object-contain rounded-t-lg", imgClassNames)}
        />
      </Show>
      <div
        className={cx(
          "flex flex-col justify-between",
          contentContainerClassNames
        )}
      >
        <div className="flex flex-col gap-6">
          <Show if={icons?.length}>
            <div className="flex items-center gap-4 w-full">
              {icons?.map(({ src, alt, classNames }, idx) => (
                <img
                  key={`verification-icon-${idx}`}
                  className={classNames}
                  src={src}
                  alt={alt}
                />
              ))}
            </div>
          </Show>
          <Show if={Boolean(title || subtitle)}>
            <div className={titleSpacingClassNames}>
              <Show if={title}>
                <h2
                  className={cx("font-bold text-content-1000", titleClassNames)}
                >
                  {title}
                </h2>
              </Show>
              <Show if={subtitle}>
                <p className={cx(subTitleClassNames)}>{subtitle}</p>
              </Show>
            </div>
          </Show>
          <Show if={Boolean(description || bulletPoints?.length)}>
            <div className="border-t border-alpha-40" />
            <Show if={description}>
              <p className={cx(textClassNames)}>{description}</p>
            </Show>
            <Show if={bulletPoints?.length}>
              <div className="flex flex-col justify-center gap-2">
                {bulletPoints?.map((bulletPointText, idx) => (
                  <div
                    key={`promo-bullet-${idx}`}
                    className="flex items-center gap-2"
                  >
                    <HugeiconsIcon
                      icon={bulletIcon ?? Tick02Icon}
                      strokeWidth={3}
                      size={16}
                      className="shrink-0 text-primary-500"
                    />
                    <p className={cx(textClassNames)}>{bulletPointText}</p>
                  </div>
                ))}
              </div>
            </Show>
          </Show>
        </div>
        <div className="flex justify-end items-center align-self-end w-full gap-2">
          <Button
            size={buttonSize}
            variant={ButtonVariant.BASE}
            onClick={() => {
              trackPromoRUM(featureName, promoType, "close-click");
              markPromoAsSeen(featureName, promoType);
              onClose?.();
            }}
            className={onCloseClassNames || closeButtonClassNames}
          >
            {closeButtonTitle ?? "Close"}
          </Button>
          <Button
            size={buttonSize}
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              trackPromoRUM(featureName, promoType, "action-click");

              if (actionButtonLink)
                window.open(actionButtonLink, "_blank", "noopener,noreferrer");

              markPromoAsSeen(featureName, promoType, "action");
              onActionButtonClick?.();
              onClose?.();

              if (actionButtonRoute) navigate(actionButtonRoute);
            }}
            className={contentActionButtonClassNames || actionButtonClassNames}
          >
            {actionButtonTitle}
          </Button>
        </div>
      </div>
    </Card>
  );
};
